import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { useFetchAfesPageQuery, useFetchUsersPageQuery } from '../../api';
import { useAuth } from '../../core';
import {
  AfeAutoComplete,
  AppInput,
  FormButtonGroup,
  UserAutoComplete,
} from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PostPurchaseOrderRequest } from '../../api/bindings/PostPurchaseOrderRequest';
import { DatePicker } from 'features';

export interface PurchaseOrderCreateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit' | 'children'> {
  onSubmit: (
    form: PostPurchaseOrderRequest & { includeRevision: boolean }
  ) => void;
  onCancel: () => void;
}

interface Form extends PostPurchaseOrderRequest {
  includeRevision: boolean;
}

export const PurchaseOrderCreateFormDrawerContent = (
  props: PurchaseOrderCreateFormDrawerContentProps
) => {
  const { onSubmit, onCancel, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: afes } = useFetchAfesPageQuery(
    {
      clientId: auth?.user.clientId ?? '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );
  const { data: users } = useFetchUsersPageQuery(
    {
      clientId: auth?.user.clientId ?? '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      afeId: '0',
      assigneeUserId: '0',
      name: '',
      description: null,
      commitmentDate: null,
      reference: null,
      includeRevision: true,
    },
  });
  const { control, register, formState, watch, handleSubmit } = form;

  const header = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>
            Create New Purchase Order: {header || 'Untitled'}
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  name="afeId"
                  control={control}
                  rules={{
                    required: 'AFE is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an AFE',
                  }}
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="asigneeUser">Asignee:</FormLabel>
                <Controller
                  name="assigneeUserId"
                  control={control}
                  rules={{
                    required: 'Asignee is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an assignee',
                  }}
                  render={(p) => (
                    <UserAutoComplete
                      id="asigneeUser"
                      items={users?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="commitmentDate">Commitment Date:</FormLabel>
                <DatePicker name="commitmentDate" control={control} />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    minLength: {
                      value: 3,
                      message: 'Reference must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Reference must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="includeRevision">
                  Include a Revision?
                </FormLabel>
                <Controller
                  name="includeRevision"
                  control={control}
                  render={(p) => (
                    <Switch
                      size="lg"
                      isChecked={p.field.value}
                      onChange={() => p.field.onChange(!p.field.value)}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onCancel}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
